import React, { useState } from 'react';
import SHA256 from 'crypto-js/sha256';

const BlockchainSimulator = () => {
  const [globalDifficulty, setGlobalDifficulty] = useState(2);
  const [chain, setChain] = useState([
    {
      index: 0,
      data: '',
      nonce: 0,
      hash: '',
      prevHash: '0',
      difficulty: 2,
      timestamp: ''
    }
  ]);

  const [modifiedBlock, setModifiedBlock] = useState(null);

  const computeHash = (block) => {
    const { index, prevHash, data, nonce, timestamp } = block;
    return SHA256(`${index}${prevHash}${data}${nonce}${timestamp}`).toString();
  };

  const isBlockValid = (block, index, entireChain) => {
    if (index === 0) {
      return block.prevHash === '0' && block.hash === computeHash(block);
    } else {
      const prevBlock = entireChain[index - 1];
      return (
        block.prevHash === prevBlock.hash &&
        block.hash === computeHash(block)
      );
    }
  };

  const handleDataChange = (index, newData) => {
    const updatedChain = [...chain];
    updatedChain[index].data = newData;

    // Mostrar alerta solo si ya tiene hash (ya fue minado)
    if (updatedChain[index].hash) {
      setModifiedBlock(index);
    }

    setChain(updatedChain);
  };

  const mineBlock = (index) => {
    const updatedChain = [...chain];
    const blockToMine = { ...updatedChain[index] };
    blockToMine.timestamp = new Date().toLocaleString();
    const targetPrefix = '0'.repeat(blockToMine.difficulty);
    let newHash = computeHash(blockToMine);

    while (!newHash.startsWith(targetPrefix)) {
      blockToMine.nonce++;
      newHash = computeHash(blockToMine);
    }

    blockToMine.hash = newHash;
    updatedChain[index] = blockToMine;

    if (index < updatedChain.length - 1) {
      updatedChain[index + 1].prevHash = blockToMine.hash;
    }

    setChain(updatedChain);
  };

  const addBlock = () => {
    const lastBlock = chain[chain.length - 1];
    const newBlock = {
      index: chain.length,
      data: '',
      nonce: 0,
      hash: '',
      prevHash: lastBlock.hash || '0',
      difficulty: globalDifficulty,
      timestamp: ''
    };
    setChain([...chain, newBlock]);
  };

  const containerStyle = {
    width: '80%',
    margin: '40px auto',
    fontFamily: 'Segoe UI, sans-serif',
    textAlign: 'left',
    margin:'5%'
  };

  const titleStyle = {
    fontSize: '2rem',
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: '20px',
    color: '#222'
  };

  const topControlStyle = {
    textAlign: 'center',
    marginBottom: '30px'
  };

  const blockGridStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '20px',
    justifyContent: 'center'
  };

  const getBlockStyle = (invalid) => ({
    width: '320px',
    backgroundColor: invalid ? '#ffdddd' : '#e8f5e9',
    border: '1px solid #ccc',
    borderRadius: '10px',
    padding: '16px',
    boxShadow: '0 2px 5px rgba(0,0,0,0.05)'
  });

  const labelStyle = {
    fontWeight: '600',
    fontSize: '1rem',
    marginBottom: '4px',
    display: 'block',
    color: '#333'
  };

  const inputStyle = {
    width: '100%',
    padding: '6px',
    marginBottom: '10px',
    border: '1px solid #ccc',
    borderRadius: '6px',
    fontSize: '0.95rem',
    fontFamily: 'Segoe UI, sans-serif'
  };

  const buttonStyle = {
    padding: '8px 16px',
    cursor: 'pointer',
    backgroundColor: '#4CAF50',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '1rem',
    fontWeight: 'bold',
    marginTop: '10px'
  };

  const modalOverlay = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 999
  };

  const modalStyle = {
    backgroundColor: '#fff',
    padding: '30px',
    borderRadius: '10px',
    width: '400px',
    textAlign: 'center',
    fontSize: '1.2rem',
    color: '#333',
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
  };

  return (
    <div style={containerStyle}>
      <h1 style={titleStyle}>Simulador de Cadena de Bloques</h1>

      <div style={topControlStyle}>
        <label style={{ fontSize: '1rem', fontWeight: 'bold', marginRight: '10px' }}>
          Dificultad del próximo bloque:
        </label>
        <input
          type="number"
          value={globalDifficulty}
          min="1"
          onChange={(e) => setGlobalDifficulty(Number(e.target.value))}
          style={{ width: '60px', marginRight: '20px', padding: '5px' }}
        />
        <button onClick={addBlock} style={buttonStyle}>
          Agregar Bloque
        </button>
      </div>

      <div style={blockGridStyle}>
        {chain.map((block, idx) => {
          const invalid = !isBlockValid(block, idx, chain);
          return (
            <div key={idx} style={getBlockStyle(invalid)}>
              <label style={labelStyle}>Bloque #{block.index}</label>

              <label style={labelStyle}>Nonce:</label>
              <input type="number" value={block.nonce} readOnly style={inputStyle} />

              <label style={labelStyle}>Datos:</label>
              <textarea
                rows="4"
                value={block.data}
                onChange={(e) => handleDataChange(idx, e.target.value)}
                style={{ ...inputStyle, resize: 'vertical' }}
              />

              <label style={labelStyle}>Hash Anterior:</label>
              <input type="text" value={block.prevHash} readOnly style={inputStyle} />

              <label style={labelStyle}>Hash:</label>
              <input type="text" value={block.hash} readOnly style={inputStyle} />

              <label style={labelStyle}>Timestamp:</label>
              <input type="text" value={block.timestamp} readOnly style={inputStyle} />

              <button onClick={() => mineBlock(idx)} style={buttonStyle}>
                Minar Bloque
              </button>
            </div>
          );
        })}
      </div>

      {modifiedBlock !== null && (
        <div style={modalOverlay}>
          <div style={modalStyle}>
            ⚠️ El bloque <strong>#{modifiedBlock}</strong> ha sido modificado.
            <br />
            Esto puede afectar la validez de la cadena.
            <br />
            <br />
            <button
              style={{ ...buttonStyle, backgroundColor: '#008CBA' }}
              onClick={() => setModifiedBlock(null)}
            >
              Entendido
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlockchainSimulator;
